import { surfaceAreaImpact } from './surface-reactions.js';
import { itemField, record, upper, type JanusCtx, type Row } from './context.js';
import { combatCenter } from '../actions/ability-preconditions.js';
import { encounterField } from '../../shared/array-types.js';
import type { JanusInput } from './janus.js';
import { placedAoeSelectionSpan, selfAoeDistance, SHAPES } from './aoe/expand-placed-aoe-targets.js';
import { upcastAreaGrowthFt } from './upcast.js';
import { validateVfxRecipe } from '../../shared/vfx-recipe-contract.js';
import { validMagicArea, magicAreaPerceived, type AreaCastVfx, type MagicArea } from '../../shared/magic-area.js';
import { entityId, privateRow, rowScalar } from '../../encounter-map/ft3d/perception.js';
import { RangeType } from '../port/k5-render/catalog.js';

/** Saved owners/delegates and one-hop controllers choose channels; each viewpoint must perceive the contact. */
export function areaVfxRecipients(area: MagicArea, encounter: Row, value: (row: Row, key: string) => unknown,
  sees: Parameters<typeof magicAreaPerceived>[2]): string[] {
  if (upper(encounterField(encounter, 'encounter_id')) !== upper(area.encounterId)) return [];
  const members = (Array.isArray(encounter.encounter_creatures_arr) ? encounter.encounter_creatures_arr : [])
    .map(record).filter((row): row is Row => !!row && !!entityId(row, 'creature')
      && upper(rowScalar(row, 'encounter_id')) === upper(area.encounterId)
      && Number(rowScalar(row, 'encounter_creature_deleted') ?? 0) !== 1);
  const byId = new Map(members.map(row => [upper(entityId(row, 'creature')), row]));
  const recipients = new Set<string>();
  for (const viewer of members) {
    if (privateRow(viewer, 'creature')) continue;
    try {
      if (!magicAreaPerceived(area, { authority: 'see-with-senses-plus-shape-los', viewerCreatureIds: [entityId(viewer, 'creature')] }, sees)) continue;
    } catch { continue; } // Incomplete scene facts grant no geometry delivery.
    // Same one-hop grant as ft_controller_grants_player_permission; a controller's own controller grants nothing.
    const controller = byId.get(upper(rowScalar(viewer, 'controller_encounter_creature_id')));
    for (const permitted of controller ? [viewer, controller] : [viewer]) {
      for (const user of [rowScalar(permitted, 'encounter_creature_created_by_id'), value(permitted, 'permissioned')]) {
        const id = upper(user);
        if (/^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(id)) recipients.add(id);
      }
    }
  }
  return [...recipients];
}

/** Called only on the successful cast branch, before effects can move the source. Cosmetic metadata owns no target identities. */
export function captureAreaVfx(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row): void {
  if (!['damage_only','end_turn_in_p_aoe','moving_into_p_aoe','dot_status_tick'].includes(String(input.useAbilityVariable1))) surfaceAreaImpact(ctx, input, ability, caster);
  if (ctx.dryRun || ['damage_only','end_turn_in_p_aoe','moving_into_p_aoe','dot_status_tick'].includes(String(input.useAbilityVariable1))) return;
  const result = validateVfxRecipe(record(ability.assets)?.magic_vfx, {});
  if (result.kind !== 'valid') return;
  const recipe = result.value;
  if (recipe.lifecycle === 'status' || recipe.lifecycle === 'zone' && recipe.areaStyle !== 'purchased-spark-ignition') return;
  const shape = SHAPES[upper(ctx.read.value(ability, 'aoe_type'))];
  if (!shape || !(recipe.kind === 'area' || recipe.areaCastCue || recipe.kind === 'burst' && !['cone','line'].includes(shape))) return;
  const selection = input.castingTargets ?? '';
  let origin: MagicArea['origin'], end: MagicArea['end'], encounterId = upper(encounterField(ctx.encounter, 'encounter_id'));
  if (selection.includes('|')) {
    const placed = placedAoeSelectionSpan(ctx.read, selection, caster, ability);
    origin = placed.span.origin; end = placed.span.endpoint; encounterId = upper(placed.encounterId);
  } else {
    const selfRange = RangeType.find(range => range.variable_name === 'self');
    if (upper(ctx.read.value(ability, 'range')) !== selfRange?.item_id || ['cone','line'].includes(shape)) return;
    const center = combatCenter(caster);
    if (!center) return;
    origin = end = center;
  }
  // The timed-finish reader has already stamped its saved level's growth before target expansion.
  const finishing = ctx.timedFinish?.casterId === upper(input.casterId) && ctx.timedFinish.abilityId === upper(input.abilityId);
  const distance = selfAoeDistance(ctx.read, caster, ability, shape, Number(ctx.read.value(ability, 'aoe_distance') ?? 0))
    + (finishing ? 0 : upcastAreaGrowthFt(ctx.read, ability, ctx.castLevel));
  const area: MagicArea = {schema:'ft.vfx.area.v1',encounterId,shape:shape as MagicArea['shape'],origin:{...origin},end:{...end},distance,
    depth:shape === 'cube' ? distance : Number(ctx.read.value(ability, 'aoe_distance_depth') ?? 1),
    height:shape === 'cube' ? distance : Number(ctx.read.value(ability, 'aoe_distance_height') ?? (shape === 'cylinder' ? 0 : 1))};
  if (!validMagicArea(area)) return;
  const cue: AreaCastVfx = {itemId:upper(itemField(ability,'item_id') ?? input.abilityId),recipe,area,
    recipientUserIds:ctx.read.areaVfxRecipients?.(area) ?? []};
  (ctx.areaCasts ??= []).push(cue);
}
export const areaVfxOf = (ctx: JanusCtx) => ctx.areaCasts?.length ? {areaCasts:ctx.areaCasts} : {};
